import { THEME_COOKIE_NAME } from '@commander/ui';
import { useEffect, useState } from 'react';
import type { ResolvedTheme, SimpleThemeMode, ThemeMode } from './types';

/**
 * Resolves theme mode to actual theme (light/dark)
 *
 * @param themeMode - User's theme preference
 * @returns Resolved theme (light/dark)
 */
export function resolveTheme(themeMode: ThemeMode): ResolvedTheme {
  if (themeMode === 'LIGHT_MODE') {
    return 'light';
  }

  if (themeMode === 'DARK_MODE') {
    return 'dark';
  }

  // SYSTEM mode - use system preference
  return getSystemTheme();
}

/**
 * Gets current system theme preference
 *
 * @returns System theme (light/dark)
 */
export function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') {
    return 'light'; // Default for SSR
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Hook to track system theme changes
 *
 * @param themeMode - Current theme mode
 * @returns Resolved theme that updates with system changes
 */
export function useSystemTheme(themeMode: ThemeMode): ResolvedTheme {
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(() => getSystemTheme());

  useEffect(() => {
    // Only listen for system theme changes if in SYSTEM mode
    if (themeMode !== 'SYSTEM') {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleThemeChange = () => {
      const newSystemTheme = mediaQuery.matches ? 'dark' : 'light';
      setSystemTheme(newSystemTheme);
    };

    // Modern browsers support addEventListener
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleThemeChange);
      return () => mediaQuery.removeEventListener('change', handleThemeChange);
    }

    // Fallback for older browsers
    if (mediaQuery.addListener) {
      mediaQuery.addListener(handleThemeChange);
      return () => mediaQuery.removeListener(handleThemeChange);
    }

    // Fallback - no cleanup needed if neither method is available
    return undefined;
  }, [themeMode]);

  // Return resolved theme based on mode
  return themeMode === 'SYSTEM' ? systemTheme : resolveTheme(themeMode);
}

/**
 * Applies theme to document root element
 *
 * @param theme - Theme to apply
 */
export function applyThemeToDocument(theme: ResolvedTheme): void {
  if (typeof document === 'undefined') {
    return;
  }

  const root = document.documentElement;

  // Remove existing theme classes
  root.classList.remove('light', 'dark');

  // Add new theme class
  root.classList.add(theme);
}

/**
 * Gets theme mode from user settings with fallback to localStorage
 *
 * @param userAppearance - User appearance setting from backend
 * @returns Theme mode with fallback to localStorage
 */
export function getThemeModeFromUserSettings(userAppearance: string | null | undefined): ThemeMode {
  if (!userAppearance) {
    return getThemeFromCookie() || 'LIGHT_MODE'; // Fallback to cookie, then light mode
  }

  const appearance = userAppearance.toUpperCase();

  if (appearance === 'LIGHT_MODE' || appearance === 'DARK_MODE') {
    return appearance as ThemeMode;
  }

  // Handle legacy values
  if (appearance === 'LIGHT') {
    return 'LIGHT_MODE';
  }

  if (appearance === 'DARK') {
    return 'DARK_MODE';
  }

  // Fallback to cookie for unrecognized values
  return getThemeFromCookie() || 'LIGHT_MODE';
}

/**
 * Gets theme mode from cookie (works across different ports)
 *
 * @returns Theme mode from cookie or null if not set
 */
export function getThemeFromCookie(): ThemeMode | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${THEME_COOKIE_NAME}=([^;]+)`));
    if (!match) {
      return null;
    }

    const stored = match[1];

    // Validate that the stored value is a valid ThemeMode
    const validThemeModes: readonly string[] = ['LIGHT_MODE', 'DARK_MODE', 'SYSTEM'];
    if (validThemeModes.includes(stored)) {
      return stored as ThemeMode;
    }

    // If the stored value is invalid, return null
    return null;
  } catch {
    return null;
  }
}

/**
 * Saves theme mode to cookie (works across different ports)
 *
 * @param mode - Theme mode to save
 */
export function saveThemeToCookie(mode: ThemeMode): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const hostname = window.location.hostname;
    const secure = window.location.protocol === 'https:' ? ';Secure' : '';

    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      // For localhost: NO domain attribute - allows sharing across all ports
      // biome-ignore lint/suspicious/noDocumentCookie: cookie needs to be shared across ports on localhost
      document.cookie = `${THEME_COOKIE_NAME}=${mode};path=/;max-age=31536000;SameSite=Lax${secure}`;
    } else {
      // For production: use dot-prefixed domain for subdomain sharing
      const domain = hostname.startsWith('www.') ? hostname.substring(4) : hostname;
      // biome-ignore lint/suspicious/noDocumentCookie: cookie needs to be shared across subdomains
      document.cookie = `${THEME_COOKIE_NAME}=${mode};path=/;domain=.${domain};max-age=31536000;SameSite=Lax${secure}`;
    }
  } catch {
    // Silently fail if cookies are not available
  }
}

/**
 * Converts simple theme mode to API format
 *
 * @param mode - Simple theme mode (light/dark/system)
 * @returns API-compatible theme mode
 */
export function simpleToApiTheme(mode: SimpleThemeMode): ThemeMode {
  switch (mode) {
    case 'light':
      return 'LIGHT_MODE';
    case 'dark':
      return 'DARK_MODE';
    case 'system':
      return 'SYSTEM';
    default:
      return 'LIGHT_MODE';
  }
}

/**
 * Converts API theme mode to simple format
 *
 * @param mode - API theme mode
 * @returns Simple theme mode
 */
export function apiToSimpleTheme(mode: ThemeMode): SimpleThemeMode {
  switch (mode) {
    case 'LIGHT_MODE':
      return 'light';
    case 'DARK_MODE':
      return 'dark';
    case 'SYSTEM':
      return 'system';
    default:
      return 'light';
  }
}

/**
 * Gets simple theme mode from cookie
 *
 * @returns Simple theme mode from cookie or null if not set
 */
export function getSimpleThemeFromCookie(): SimpleThemeMode | null {
  const theme = getThemeFromCookie();
  return theme ? apiToSimpleTheme(theme) : null;
}

/**
 * Saves simple theme mode to cookie
 *
 * @param mode - Simple theme mode to save
 */
export function saveSimpleThemeToCookie(mode: SimpleThemeMode): void {
  const apiMode = simpleToApiTheme(mode);
  saveThemeToCookie(apiMode);
}
